import { IconAlertTriangle } from '@tabler/icons-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import type {
  CampaignAudienceBrandPreview,
  CampaignAudienceMode,
  CampaignAudiencePreviewResponse,
} from '@/features/campaign-targeting/preview-state';
import { publicPreviewErrorMessage } from '@/features/campaign-targeting/preview-state';

interface CampaignAudiencePreviewProps {
  preview: CampaignAudiencePreviewResponse;
  showBrandErrors?: boolean;
}

const suppressionLabels: Record<string, string> = {
  optedOut: 'opted out of marketing',
  noAppUser: 'without an app user account',
  inactiveAppUser: 'with an inactive app user profile',
  noEmail: 'without an email address',
  unverifiedEmail: 'with an unverified email address',
  duplicateEmail: 'sharing an address with another app user',
};

export function CampaignAudiencePreview({
  preview,
  showBrandErrors = true,
}: CampaignAudiencePreviewProps) {
  return (
    <section aria-label="Audience preview evidence" className="mb-6 space-y-4">
      {preview.brands.map((brand) => (
        <BrandEvidence
          key={brand.mvnoId}
          brand={brand}
          audienceMode={preview.audienceMode ?? 'legacy-live'}
          showError={showBrandErrors}
        />
      ))}
    </section>
  );
}

function BrandEvidence({
  brand,
  audienceMode,
  showError,
}: {
  brand: CampaignAudienceBrandPreview;
  audienceMode: CampaignAudienceMode;
  showError: boolean;
}) {
  const freshness = brand.freshness;
  const coverage = brand.coverage;
  const quality = brand.quality;
  const latestEdr =
    freshness?.latestRawIngestedAt ?? brand.latestEdrIngestedAt ?? null;
  return (
    <article className="space-y-3 rounded-lg border border-border p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge>{brand.brandLabel}</Badge>
        {brand.snapshotId && (
          <span className="text-xs text-muted-foreground">
            Snapshot: {brand.snapshotId}
          </span>
        )}
      </div>

      {brand.error && showError && (
        <Alert variant="destructive">
          <AlertDescription>
            Audience preview unavailable — {publicPreviewErrorMessage(brand)}
          </AlertDescription>
        </Alert>
      )}
      {brand.activityDataStatus === 'UNAVAILABLE' && (
        <Alert variant="warning">
          <IconAlertTriangle />
          <AlertDescription>
            Activity data unavailable. Activity-based counts are not zero; they
            cannot be established from this snapshot.
          </AlertDescription>
        </Alert>
      )}

      {audienceMode === 'legacy-live' && (
        <Alert variant="info">
          <AlertDescription>
            Live Nova CDC audience. Counts are recalculated when the campaign is
            sent.
          </AlertDescription>
        </Alert>
      )}

      {audienceMode === 'snapshot' && (
        <>
          <div className="grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
            <p>Cutoff: {formatDate(freshness?.cutoffAt ?? brand.cutoffAt)}</p>
            <p>Latest EDR ingestion: {formatDate(latestEdr)}</p>
            <p>Source lag: {formatHours(freshness?.sourceLagHours)}</p>
            <p>
              Account match rate: {formatPercent(quality?.accountMatchRate)}
            </p>
          </div>

          <div>
            <p className="text-sm font-medium">Classifier coverage</p>
            <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
              <span>
                Data {formatPercent(coverage?.dataIdentifierCoverage)}
              </span>
              <span>
                Voice {formatPercent(coverage?.voiceIdentifierCoverage)}
              </span>
              <span>SMS {formatPercent(coverage?.smsIdentifierCoverage)}</span>
            </div>
            {coverage?.smsIdentifierCoverage === 0 && (
              <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                Warning: SMS classifier coverage is 0%; SMS activity evidence is
                incomplete.
              </p>
            )}
          </div>

          {quality?.qualityWarnings.map((warning) => (
            <p
              key={warning}
              className="flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400"
            >
              <IconAlertTriangle size={13} className="mt-0.5 shrink-0" />
              {warning}
            </p>
          ))}
        </>
      )}

      <div className="space-y-2">
        {brand.channels.map((entry) => (
          <div
            key={`${brand.mvnoId}-${entry.channel}`}
            className="rounded-lg border border-border p-3"
          >
            <div className="mb-1 flex items-center gap-2">
              <Badge variant="secondary">{entry.channel}</Badge>
              <span className="text-sm font-medium">
                {formatCount(entry.eligible)} eligible
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {entry.suppressed.length === 0
                ? 'No suppressions reported.'
                : `Suppressed — ${entry.suppressed
                    .map(
                      ({ reason, count }) =>
                        `${formatCount(count)} ${suppressionLabels[reason] ?? reason}`,
                    )
                    .join(', ')}`}
            </p>
          </div>
        ))}
      </div>
    </article>
  );
}

function formatCount(value: number): string {
  return value.toLocaleString('en-US');
}

function formatDate(value: string | null | undefined): string {
  if (!value) return 'Unavailable';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Unavailable';
  return date.toLocaleString('en-ZA', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Africa/Johannesburg',
  });
}

function formatHours(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value))
    return 'Unavailable';
  return `${value.toLocaleString('en-US')} ${value === 1 ? 'hour' : 'hours'}`;
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value))
    return 'Unavailable';
  return `${(value * 100).toLocaleString('en-US', { maximumFractionDigits: 1 })}%`;
}
